import { useState, useEffect, useRef, useCallback } from 'react'
import { apiFetch, ApiError } from '../../../platform/lib/apiClient'
import type { AuditProgress } from '../types/audit'

// Spec 078 — suivi de la progression d'un audit. Polling ~30 s tant que l'exécution est en cours,
// arrêt dès l'état terminal (FR-003, aligné latence d'ingestion LA). L'appel LA est côté serveur.

const POLL_INTERVAL_MS = 30_000

interface UseAuditProgressState {
  progress: AuditProgress | null
  loading: boolean
  error: string | null
  refresh: () => void
  /** Horodatage (ms epoch) de la prochaine actualisation auto, ou null si le polling est arrêté. */
  nextRefreshAt: number | null
}

/**
 * @param accountId compte à suivre, ou null pour désactiver (volet fermé).
 */
export function useAuditProgress(accountId: string | null): UseAuditProgressState {
  const [progress, setProgress] = useState<AuditProgress | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nextRefreshAt, setNextRefreshAt] = useState<number | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const activeRef = useRef(false)

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  const schedule = useCallback((id: string) => {
    clearTimer()
    timerRef.current = setTimeout(() => { void fetchOnceRef.current(id) }, POLL_INTERVAL_MS)
    if (activeRef.current) setNextRefreshAt(Date.now() + POLL_INTERVAL_MS)
  }, [])

  const fetchOnce = useCallback(async (id: string) => {
    try {
      const data = await apiFetch<AuditProgress>(`/api/audit/${id}/progress`)
      if (!activeRef.current) return
      setProgress(data)
      setError(null)
      // Re-planifier uniquement si non terminal ; sinon arrêt du polling.
      if (!data.terminal) {
        schedule(id)
      } else {
        clearTimer()
        setNextRefreshAt(null)
      }
    } catch (err) {
      if (!activeRef.current) return
      const msg = err instanceof ApiError ? (err.message ?? err.code) : 'Erreur lors du suivi.'
      setError(msg)
      // En cas d'erreur réseau, réessayer au prochain intervalle (le suivi reste vivant).
      schedule(id)
    } finally {
      if (activeRef.current) setLoading(false)
    }
  }, [schedule])

  // Réf. stable vers la dernière version de fetchOnce (utilisée par le setTimeout de `schedule`).
  const fetchOnceRef = useRef(fetchOnce)
  fetchOnceRef.current = fetchOnce

  const refresh = useCallback(() => {
    if (!accountId) return
    setLoading(true)
    void fetchOnce(accountId)
  }, [accountId, fetchOnce])

  useEffect(() => {
    activeRef.current = true
    clearTimer()
    setProgress(null)
    setError(null)
    setNextRefreshAt(null)
    if (accountId) {
      setLoading(true)
      void fetchOnce(accountId)
    }
    return () => {
      activeRef.current = false
      clearTimer()
    }
  }, [accountId, fetchOnce])

  return { progress, loading, error, refresh, nextRefreshAt }
}
